import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SrIcon } from "@/components/ui/SrIcon";
import { EnrichmentCandidates } from "@/components/equipment/EnrichmentCandidates";
import { useApplyEnrichment, useDiscardEnrichment, useLatestEnrichmentRun, useStartEnrichment } from "@/hooks/use-equipment-enrichment";
import { useLinkEquipmentProduct, useSetProductUrl } from "@/hooks/use-equipment-products";
import { type CandidateSelection, defaultSelection, isRunInProgress } from "@/lib/equipment/enrichment";
import { checkProductFields, parseProductUrl } from "@/lib/equipment/product-link";
import type { DBEquipmentProduct } from "@/lib/db-types";

interface EnrichmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: DBEquipmentProduct | null;
  /** Fra utstyrskortet: lar brukeren koble utstyret til en modell først. */
  equipmentId?: string;
  /** Start hentingen straks når modellen aldri er hentet for. Ellers velger brukeren «Hent på nytt». */
  autoStart?: boolean;
}

/**
 * «Hent produktdata» (mockup K). Henter et forslag med Firecrawl, viser det med
 * avkrysning og lagrer bare det som er valgt. «Forkast» lagrer ingenting.
 */
export function EnrichmentDialog({ open, onOpenChange, product, equipmentId, autoStart }: EnrichmentDialogProps) {
  const productId = product?.id ?? null;
  const { data: run, isLoading } = useLatestEnrichmentRun(productId, open);
  const start = useStartEnrichment();
  const apply = useApplyEnrichment();
  const discard = useDiscardEnrichment();
  const link = useLinkEquipmentProduct();
  const setUrl = useSetProductUrl();
  const [selection, setSelection] = useState<CandidateSelection | null>(null);
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [productUrl, setProductUrl] = useState(product?.product_url ?? "");
  const autoStarted = useRef<string | null>(null);

  const ready = run?.status === "ready" && run.candidates ? run : null;
  const inProgress = isRunInProgress(run);

  useEffect(() => {
    if (ready?.candidates) setSelection(defaultSelection(ready.candidates));
  }, [ready?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !autoStart || !productId || isLoading || run || start.isPending) return;
    if (autoStarted.current === productId) return;
    autoStarted.current = productId;
    start.mutate(productId, { onError: (e) => toast.error(e.message) });
  }, [open, autoStart, productId, isLoading, run, start]);

  useEffect(() => {
    if (!open) autoStarted.current = null;
    else setProductUrl(product?.product_url ?? "");
  }, [open, product?.product_url]);

  /** Lagrer en ny eller rettet produktlenke på modellen før hentingen, så den hentes fra lenken (VIB-2936). */
  const fetchNow = async () => {
    if (!productId) return;
    const parsed = parseProductUrl(productUrl);
    if ("error" in parsed) return toast.error(parsed.error);
    try {
      if (parsed.url !== (product?.product_url ?? null)) await setUrl.mutateAsync({ productId, productUrl: parsed.url });
    } catch (err) {
      return toast.error((err as Error).message);
    }
    start.mutate(productId, { onError: (e) => toast.error(e.message) });
  };

  const linkAndFetch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!equipmentId) return;
    const checked = checkProductFields({ brand, model, productUrl });
    if ("error" in checked) return toast.error(checked.error);
    if (!checked.link) return toast.error("Fyll ut merke og modell");
    try {
      const id = await link.mutateAsync({ equipmentId, ...checked.link });
      // Hindrer at autostarten henter en gang til hvis denne hentingen feiler.
      autoStarted.current = id;
      start.mutate(id, { onError: (err) => toast.error(err.message) });
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const save = () => {
    if (!ready || !selection || !productId) return;
    apply.mutate(
      {
        runId: ready.id,
        productId,
        images: selection.images,
        documents: selection.documents,
        specs: selection.includeSpecs ? selection.specs.filter((s) => s.label.trim() && s.value.trim()) : null,
        description: selection.includeDescription ? ready.candidates?.description ?? null : null,
      },
      {
        onSuccess: (res) => {
          if (res.failed > 0) {
            const reasons = res.results.filter((r) => !r.ok).map((r) => r.reason).join(", ");
            toast.warning(`${res.saved} lagret, ${res.failed} feilet: ${reasons}`);
          } else {
            toast.success("Produktdata er lagret");
          }
          onOpenChange(false);
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  const forkast = () => {
    if (!ready || !productId) return onOpenChange(false);
    discard.mutate({ runId: ready.id, productId }, { onSuccess: () => onOpenChange(false), onError: (e) => toast.error((e as Error).message) });
  };

  const count = selection ? selection.images.length + selection.documents.length : 0;
  const title = product ? `Produktdata for ${product.brand_name} ${product.model}` : "Hent produktdata";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {product ? "Bilder, manualer og spesifikasjoner fra produsenten. Ingenting lagres før du velger." : "Velg merke og modell. Modellen deles av alt utstyr av samme slag."}
          </DialogDescription>
        </DialogHeader>

        {!product && equipmentId && (
          <form onSubmit={linkAndFetch} className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="enrich-brand">Merke</Label>
                <Input id="enrich-brand" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="f.eks. Røde" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="enrich-model">Modell</Label>
                <Input id="enrich-model" value={model} onChange={(e) => setModel(e.target.value)} placeholder="f.eks. NTG-3B" />
              </div>
            </div>
            <ProductUrlField value={productUrl} onChange={setProductUrl} />
            <DialogFooter>
              <Button type="submit" disabled={link.isPending || !brand.trim() || !model.trim()}>Hent produktdata</Button>
            </DialogFooter>
          </form>
        )}

        {product && (start.isPending || inProgress || isLoading) && (
          <div className="flex items-center gap-3 py-10 justify-center text-sm text-muted-foreground" role="status">
            <SrIcon name="loader2" className="h-5 w-5 animate-spin" />
            Henter fra produsenten … Det tar vanligvis 10–20 sekunder.
          </div>
        )}

        {product && !start.isPending && !isLoading && !ready && !inProgress && (
          <div className="space-y-3 py-4 text-sm">
            {run?.status === "failed" && <p className="text-destructive">Forrige henting feilet: {run.error}</p>}
            {run?.status === "running" && <p className="text-destructive">Forrige henting ble aldri ferdig.</p>}
            {run?.status === "applied" && <p className="text-muted-foreground">Produktdata er lagret for modellen. Du kan hente på nytt; lagrede filer overskrives ikke.</p>}
            <ProductUrlField value={productUrl} onChange={setProductUrl} />
            <Button onClick={fetchNow} disabled={setUrl.isPending}>
              <SrIcon name="download" className="h-4 w-4" />
              {productUrl.trim() ? "Hent fra lenke" : run ? "Hent på nytt" : "Hent produktdata"}
            </Button>
          </div>
        )}

        {ready?.candidates && selection && !start.isPending && (
          <>
            <EnrichmentCandidates candidates={ready.candidates} selection={selection} onChange={setSelection} />
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={forkast} disabled={apply.isPending || discard.isPending}>Forkast</Button>
              <Button onClick={save} disabled={apply.isPending || (count === 0 && !selection.includeSpecs && !selection.includeDescription)}>
                {apply.isPending ? "Lagrer …" : `Lagre valgte${count > 0 ? ` (${count})` : ""}`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ProductUrlField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor="enrich-product-url">Produktlenke</Label>
      <Input id="enrich-product-url" type="url" inputMode="url" value={value} onChange={(e) => onChange(e.target.value)} placeholder="https://… (produsentens side for akkurat denne modellen)" />
      <p className="text-xs text-muted-foreground">Med lenke hentes data fra den siden i stedet for et søk, så varianten blir riktig.</p>
    </div>
  );
}
